import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { rebuild } from 'react-tooltip'

import { PageHeading } from '../../../../components'
import Table, { Col, TableFooter } from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'
import ReportMenu from '../../Menu'
import VerticalSummaryFilterDisplay from '../Filters'

import { prepareDatesForApi } from '../../../../utils/dates'

import {
  VerticalSummaryGroupBy,
  VerticalSummaryTableContext,
  VerticalSummaryVariables,
} from './index'
import { State } from '../../../../reducers'
import { UserStore } from '../../../../typings/stores/UserStore'

import styles from './styles.module.css'
import { VerticalSummaryReportingQueryVariables } from '../../../../generated/types'
import { VerticalSummary } from '../../../../typings/detailReport'
import accounting from 'accounting'
import { GenerateDetailReportLink } from '../../../../components/LinkRow'
import { DateTime } from 'luxon'
import DateDisplay from '../../../../components/DateDisplay'
import { dumpData } from '../../../../actions/reports'
import { decode } from 'he'

interface VerticalSummaryTableProps {
  groupBy: VerticalSummaryGroupBy
  cols: Col[]
  footerPadding: number
  data: any
  loading: boolean
  error: boolean
  getData: ({ variables }: { variables: VerticalSummaryVariables }) => void
  exportData: any
  exportLoading: boolean
  exportError: boolean
  getExportData: ({
    variables,
  }: {
    variables: VerticalSummaryVariables
  }) => void
}

const VerticalSummaryTable: FunctionComponent<VerticalSummaryTableProps> = ({
  groupBy,
  cols,
  footerPadding,
  getData,
  data,
  loading,
  error,
  getExportData,
  exportData,
  exportLoading,
  exportError,
}) => {
  const user = useSelector<State, UserStore>(state => state.user)
  const {
    changePage,
    changePageSize,
    filters,
    pagination: { size, page },
    setFilters,
    sort,
    changeSort,
  } = useContext(VerticalSummaryTableContext)
  // const dispatch = useDispatch()
  useEffect(() => {
    rebuild()
  })
  const updateVariables = () => {
    const { startDate, endDate } = filters
    const queryVariables = {
      ...prepareDatesForApi(startDate, endDate),
      skip: (page - 1) * size,
      take: size,
      groupBy: filters.groupBy,
      externalPropertyId: user.activeWebsite.id.toString(),
      externalVerticalId: filters.vertical ? filters.vertical : '5',
      externalCampaignId: filters?.campaign,
      externalClickedProductId: filters.clickedProduct,
      externalDisplayedProductId: filters.product,
      externalProductTypeId: filters.productType
        ? parseInt(filters.productType)
        : null,
      externalProgramId: filters.program,
      subTracking: filters.subTracking,
    } as VerticalSummaryReportingQueryVariables
    return sort.dir
      ? {
          ...queryVariables,
          sorts: [
            sort.by === 'productTypeId'
              ? {
                  [sort.by]: sort.dir.toUpperCase(),
                  productTypeName: sort.dir.toUpperCase(),
                }
              : { [sort.by]: sort.dir.toUpperCase() },
          ],
        }
      : queryVariables
  }

  const fetchData = useCallback(() => {
    getData({ variables: updateVariables() })
  }, [filters, size, page, sort, user.activeWebsite])

  const fetchExport = () => {
    getExportData({
      variables: {
        ...updateVariables(),
        take: get(data, `verticalSummaryReporting.pagination.totalEntries`, 0),
      },
    })
  }

  useEffect(() => {
    dumpData('verticalSummary', exportData?.verticalSummaryReporting?.page, {
      ...filters,
    })
  }, [exportData])

  useEffect(fetchData, [sort, page, size, user.activeWebsite])

  const getTotal = (data, param) =>
    data.reduce((sum, page) => sum + page[param], 0)

  const pagination = {
    currentPage: get(
      data,
      `verticalSummaryReporting.pagination.currentPage`,
      1
    ),
    pageSize: get(data, `verticalSummaryReporting.pagination.pageSize`, 10),
    total: get(data, `verticalSummaryReporting.pagination.totalEntries`, 0),
  }

  const totalRowBuilder: TableFooter[] = [
    {
      text: 'Totals',
      colSpan: footerPadding - 4,
      className: styles.footerHeader,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.verticalSummaryReporting.page, 'totalImpressions')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.verticalSummaryReporting.page, 'viewableImpressions')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.verticalSummaryReporting.page, 'productClicks')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.verticalSummaryReporting.page, 'uniqueClicks')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? (
            getTotal(data.verticalSummaryReporting.page, 'totalCTR') * 100
          ).toFixed(2) + '%'
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? (
            getTotal(data.verticalSummaryReporting.page, 'viewableCTR') * 100
          ).toFixed(2) + '%'
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.verticalSummaryReporting.page, 'sales')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? (
            getTotal(data.verticalSummaryReporting.page, 'conversionRate') * 100
          ).toFixed(2) + '%'
        : 0,
      className: styles.right,
    },
    {
      text:
        data &&
        accounting.formatMoney(
          getTotal(data.verticalSummaryReporting.page, 'commission'),
          '$'
        ),
      className: styles.right,
    },
    {
      text:
        data &&
        accounting.formatMoney(
          getTotal(data.verticalSummaryReporting.page, 'epc'),
          '$'
        ),
      className: styles.right,
    },
  ]
  if (filters.vertical === '5') {
    cols.splice(
      7,
      0,
      {
        text: 'Applications',
        field: 'totalApplications',
        sortable: true,
        render: row => (
          <GenerateDetailReportLink
            whereCriteria="totalApplications"
            id={get(row, 'totalApplications', '')}
            value={accounting.formatNumber(get(row, 'totalApplications', ''))}
            detailPath="/new/reports/event-details"
            rightAligment={true}
            queryParams={[
              {
                startDate: get(row, 'day', '') ?? filters.startDate,
                endDate: get(row, 'day', '') ?? filters.endDate,
                start: get(row, 'day', '') ?? filters.startDate,
                end: get(row, 'day', '') ?? filters.endDate,
                dateRange: 'custom',
                dateType: 'clickDate',
                event: 'app',
                vertical: filters.vertical,
                program: get(row, 'programId', '') ?? filters.program,
                product:
                  get(row, 'displayedProductId', '') ??
                  filters.displayedProduct,
                productType:
                  get(row, 'productTypeId', '') ?? +filters.productType,
                campaign: get(row, 'campaignId', '') ?? filters.campaign,
                clickedProduct:
                  get(row, 'clickedProductId', '') ?? filters.clickedProduct,
                subTracking: get(row, 'subTracking', '') ?? filters.subTracking,
              },
            ]}
          />
        ),
        className: styles.right,
      },
      {
        text: 'Declines',
        field: 'totalDeclines',
        sortable: true,
        render: row => (
          <GenerateDetailReportLink
            whereCriteria="totalDeclines"
            id={get(row, 'totalDeclines', '')}
            value={accounting.formatNumber(get(row, 'totalDeclines', ''))}
            detailPath="/new/reports/event-details"
            rightAligment={true}
            queryParams={[
              {
                startDate: get(row, 'day', '') ?? filters.startDate,
                endDate: get(row, 'day', '') ?? filters.endDate,
                start: get(row, 'day', '') ?? filters.startDate,
                end: get(row, 'day', '') ?? filters.endDate,
                dateRange: 'custom',
                dateType: 'clickDate',
                event: 'dec',
                vertical: filters.vertical,
                program: get(row, 'programId', '') ?? filters.program,
                product: get(row, 'productId', '') ?? filters.displayedProduct,
                productType:
                  get(row, 'productTypeId', '') ?? +filters.productType,
                campaign: get(row, 'campaignId', '') ?? filters.campaign,
                clickedProduct:
                  get(row, 'clickedProductId', '') ?? filters.clickedProduct,
                subTracking: get(row, 'subTracking', '') ?? filters.subTracking,
              },
            ]}
          />
        ),
        className: styles.right,
      }
    )
    totalRowBuilder.splice(
      7,
      0,
      {
        text: data
          ? accounting.formatNumber(
              getTotal(data.verticalSummaryReporting.page, 'totalApplications')
            )
          : 0,
        className: styles.right,
      },
      {
        text: data
          ? accounting.formatNumber(
              getTotal(data.verticalSummaryReporting.page, 'totalDeclines')
            )
          : 0,
        className: styles.right,
      }
    )
  }
  if (filters.groupBy === 'byDate') {
    cols.shift()
    cols.unshift({
      text: 'Date',
      field: 'day',
      sortable: true,
      render: row => (
        <DateDisplay datetime={DateTime.fromISO(get(row, 'day', ''))} />
      ),
      className: styles.center,
    })
  }

  if (filters.groupBy === 'byDisplayedProduct') {
    cols.shift()
    cols.unshift({
      text: 'Name',
      field: 'displayedProductName',
      sortable: true,
      render: row =>
        get(row, 'displayedProductName', '')
          ? decode(get(row, 'displayedProductName', ''))
          : 'N/A',
    })
  }

  if (filters.groupBy === 'byProgram') {
    cols.shift()
    cols.unshift({
      text: 'Name',
      field: 'programName',
      sortable: true,
      render: row =>
        get(row, 'programName', '')
          ? decode(get(row, 'programName', ''))
          : 'N/A',
    })
  }

  if (filters.groupBy === 'byClickedProduct') {
    cols.shift()
    cols.unshift({
      text: 'Name',
      field: 'clickedProductName',
      sortable: true,
      render: row =>
        get(row, 'clickedProductName', '')
          ? decode(get(row, 'clickedProductName', ''))
          : 'N/A',
    })
  }

  if (filters.groupBy === 'byCampaign') {
    cols.shift()
    cols.unshift({
      text: 'Name',
      field: 'campaignName',
      sortable: true,
      render: row =>
        get(row, 'campaignName', '')
          ? decode(get(row, 'campaignName', ''))
          : 'N/A',
    })
  }

  if (filters.groupBy === 'bySubTracking') {
    cols.shift()
    cols.unshift({
      text: 'SubTracking',
      field: 'subTracking',
      sortable: true,
      render: row =>
        get(row, 'subTracking', '')
          ? decode(get(row, 'subTracking', ''))
          : 'N/A',
    })
  }

  const [propertyHasData, setPropertyHasData] = useState(true)
  const [pageDescription, setPageDescription] = useState(
    'This report contains aggregated information for clicks, sales, and commissions filtered by a specified date range.'
  )

  useEffect(() => {
    if (!propertyHasData) {
      setPageDescription('No reporting data available for this property yet.')
    }
  }, [propertyHasData])

  useEffect(() => {
    if (!propertyHasData) {
      window.location.reload()
    }
  }, [user.activeWebsite])

  return (
    <>
      <ReportMenu />

      <PageHeading
        title="Vertical Summary"
        description={pageDescription}
      ></PageHeading>

      {propertyHasData && (
        <>
          <VerticalSummaryFilterDisplay
            filters={filters}
            setFilters={setFilters}
            changeSort={changeSort}
            setPropertyHasData={setPropertyHasData}
          />

          <ReportActions
            fetchReport={fetchData}
            fetchExport={fetchExport}
            subscriptionVariables={updateVariables()}
            disabled={
              !get(data, `verticalSummaryReporting.pagination.totalEntries`, 0)
            }
            reportStatus={{ loading: loading, hasError: error }}
            exportStatus={{ loading: exportLoading, hasError: exportError }}
          />

          <Table<VerticalSummary[]>
            className={styles.table}
            data={data && get(data, `verticalSummaryReporting.page`)}
            cols={cols}
            totalRow={totalRowBuilder}
            sort={sort}
            onSortChange={changeSort}
            pagination={{
              onPageSizeUpdate: changePageSize,
              onPaginate: changePage,
              ...pagination,
            }}
            isLoading={loading}
          />
        </>
      )}
    </>
  )
}

export default VerticalSummaryTable
